import { useReduxState } from 'hooks';
import { ChangeEventHandler, CSSProperties, KeyboardEvent } from 'react';

export type TextInputProps = {
  darkMode?: boolean;
  disabled?: boolean;
  error?: string;
  id?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onFocus?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  style?: CSSProperties;
  type?: string;
  value?: string;
};

const TextInput = ({
  darkMode,
  error,
  id,
  onChange,
  placeholder,
  style,
  type = 'text',
  value,
  ...params
}: TextInputProps) => {
  const { theme } = useReduxState();

  let color = theme.textColor;
  let backgroundColor = '#fff';
  let backgroundColorDisabled = theme.lightGreyLighter;
  let borderColor = theme.lightGreyDark;
  let borderColorActive = '#a1a8aa';
  let errorColor = theme.dangerColor;
  let placeholderColor = '#999';
  if (darkMode) {
    color = '#fff';
    backgroundColor = 'rgba(0, 0, 0, 0.2)';
    backgroundColorDisabled = 'rgba(255, 255, 255, 0.2)';
    borderColor = 'rgba(255, 255, 255, 0.3)';
    borderColorActive = 'rgba(255, 255, 255, 0.6)';
    errorColor = theme.dangerColor;
    placeholderColor = '#ccc';
  }

  return (
    <>
      <input
        {...params}
        className="input"
        id={id}
        onChange={onChange}
        placeholder={placeholder}
        style={style}
        type={type}
        value={value}
      />

      <style jsx>{`
        .input {
          background-color: ${backgroundColor};
          border: 1px solid ${error ? errorColor : borderColor};
          border-radius: ${theme.inputBorderRadius};
          box-shadow: none;
          color: ${color};
          font-family: inherit;
          font-size: 1.5rem;
          outline: 0;
          padding: 7px 10px;
          position: relative;
          transition: border-color 0.2s;
          width: 100%;
        }
        .input:focus {
          border-color: ${error ? errorColor : borderColorActive};
          box-shadow: none;
        }
        .input:disabled {
          background-color: ${backgroundColorDisabled};
          color: ${theme.textColorLighter};
          opacity: 0.7;
        }
        .input::-webkit-input-placeholder {
          color: ${placeholderColor};
          opacity: 1;
        }
        .input::-moz-placeholder {
          color: ${placeholderColor};
          opacity: 1;
        }
        .input::-ms-input-placeholder {
          color: ${placeholderColor};
          opacity: 1;
        }
        .input:-ms-input-placeholder {
          color: ${placeholderColor};
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default TextInput;
